import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import { PreOrderFlowStatus } from "@/app/api/types/preOrderFlow";

export interface ValidationError {
  key: string;
  lineNumber?: number;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export interface ValidationIssue {
  preOrderId: string;
  preOrderNumber: string | null;
  partnerName: string | null;
  emptyFields: ValidationError[];
}

export function validateFlow(
  flow: PreOrderFlow,
  lineNumber?: number,
): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!flow.product_id) errors.push({ key: "noProductSelected", lineNumber });
  if (!(flow.quantity != null && flow.quantity > 0))
    errors.push({ key: "noQuantity", lineNumber });
  if (!(flow.weight != null && flow.weight > 0))
    errors.push({ key: "noWeight", lineNumber });
  if (!(flow.price_per_weight != null && flow.price_per_weight > 0))
    errors.push({ key: "noPrice", lineNumber });
  return errors;
}

export function validatePreOrder(preOrder: PreOrder): ValidationResult {
  const errors: ValidationError[] = [];
  (preOrder.flows || []).forEach((flow, i) => {
    errors.push(...validateFlow(flow, i + 1));
  });
  return { isValid: errors.length === 0, errors };
}

export function validatePreOrders(preOrders: PreOrder[]): ValidationIssue[] {
  return preOrders
    .map((po) => {
      const result = validatePreOrder(po);
      if (result.isValid) return null;
      return {
        preOrderId: po.id,
        preOrderNumber: po.pre_order_number || null,
        partnerName: po.partner?.name || null,
        emptyFields: result.errors,
      };
    })
    .filter((issue): issue is ValidationIssue => issue !== null);
}

export function hasEmptyFields(preOrder: PreOrder): boolean {
  return !validatePreOrder(preOrder).isValid;
}

export function hasAnyEmptyFields(preOrders: PreOrder[]): boolean {
  return preOrders.some((po) => hasEmptyFields(po));
}

export function isPreOrderComplete(preOrder: PreOrder): boolean {
  return validatePreOrder(preOrder).isValid;
}

export function filterCompletePreOrders(preOrders: PreOrder[]): PreOrder[] {
  return preOrders.filter(isPreOrderComplete);
}

export function filterIncompletePreOrders(preOrders: PreOrder[]): PreOrder[] {
  return preOrders.filter((po) => !isPreOrderComplete(po));
}

export type ZeroQuantityField = "quantity" | "weight" | "unit_id";

/**
 * Returns true if any non-cancelled flow has a missing or zero value among
 * the requested fields. Used to warn before confirming orders that would
 * block M3 (ERP).
 *
 * The caller passes `fieldsToCheck` explicitly so this service stays UI-
 * agnostic — recap components hide weight/price columns via feature flags
 * and decide which fields to validate accordingly.
 *
 * Accepts a flat array of flows — callers are responsible for providing
 * the freshest data (e.g. localFlows in PreOrderContent, or
 * preOrders.flatMap(po => po.flows || []) elsewhere).
 */
export function hasZeroQuantityInFlows(
  flows: PreOrderFlow[],
  fieldsToCheck: ReadonlyArray<ZeroQuantityField>,
): boolean {
  if (fieldsToCheck.length === 0) return false;
  return flows.some((f) => {
    if (f.status === PreOrderFlowStatus.CANCELLED) return false;
    return fieldsToCheck.some((field) => {
      if (field === "unit_id") return !f.unit_id;
      const value = f[field];
      return value == null || Number(value) === 0;
    });
  });
}

/**
 * Returns true if any non-cancelled flow has a missing or zero
 * `price_per_weight`. Used by the recap confirm flow on PURCHASE pre-orders
 * (category=1) to surface a warning BEFORE the round-trip to M3 —
 * `_validate_purchase_prices` in the M3 bridge will reject the export
 * anyway (ghost-PO regression, see STK-558), but the dialog gives the
 * user an inline UX with explicit "confirm anyway" semantics.
 *
 * Sales pre-orders (category=0) are NOT gated by this: M3 sales orders
 * accept null prices (contracts derive them), and the existing
 * `hasZeroQuantityInFlows` already covers the quantity dimension that
 * matters for sales.
 */
export function hasZeroPriceInFlows(flows: PreOrderFlow[]): boolean {
  return flows.some((f) => {
    if (f.status === PreOrderFlowStatus.CANCELLED) return false;
    const v = f.price_per_weight;
    return v == null || Number(v) === 0;
  });
}

/**
 * A single per-line completeness miss, carrying WHICH line and WHICH field so
 * the warning dialogs can name them ("Ligne 2 — Trafic manquant") instead of a
 * generic sentence. `fieldKey` is stable (for i18n / testing); `fieldLabel` is
 * the already-human label (a custom field's own label, or a translated field
 * name resolved by the caller).
 */
export interface LineFieldIssue {
  lineNumber: number;
  fieldKey: string;
  fieldLabel: string;
}

type RequiredDef = { key: string; label: string; required?: boolean };

function isBlank(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
}

/**
 * Per-line list of REQUIRED custom fields/columns left empty. `fieldDefs` are
 * the popover custom fields (Trafic, Type marchandise), `columnDefs` the inline
 * custom columns (Rolls, Pallets) — both stored on `flow_metadata[key]`. Only
 * defs flagged `required` are checked; cancelled flows are skipped.
 */
export function findMissingRequiredCustomFields(
  flows: PreOrderFlow[],
  fieldDefs: ReadonlyArray<RequiredDef> = [],
  columnDefs: ReadonlyArray<RequiredDef> = [],
): LineFieldIssue[] {
  const required = [...fieldDefs, ...columnDefs].filter((d) => d.required);
  if (required.length === 0) return [];
  const issues: LineFieldIssue[] = [];
  flows.forEach((flow, i) => {
    if (flow.status === PreOrderFlowStatus.CANCELLED) return;
    const meta = (flow.flow_metadata ?? {}) as Record<string, unknown>;
    for (const def of required) {
      if (isBlank(meta[def.key])) {
        issues.push({
          lineNumber: i + 1,
          fieldKey: def.key,
          fieldLabel: def.label,
        });
      }
    }
  });
  return issues;
}

/**
 * Per-line list of zero/missing values among the requested standard fields.
 * Structured twin of `hasZeroQuantityInFlows` — `fieldLabel` is left as the
 * stable field key for the caller to translate (quantity/weight/unit).
 */
export function findZeroQuantityIssues(
  flows: PreOrderFlow[],
  fieldsToCheck: ReadonlyArray<ZeroQuantityField>,
): LineFieldIssue[] {
  if (fieldsToCheck.length === 0) return [];
  const issues: LineFieldIssue[] = [];
  flows.forEach((f, i) => {
    if (f.status === PreOrderFlowStatus.CANCELLED) return;
    for (const field of fieldsToCheck) {
      const empty =
        field === "unit_id"
          ? !f.unit_id
          : f[field] == null || Number(f[field]) === 0;
      if (empty) {
        issues.push({ lineNumber: i + 1, fieldKey: field, fieldLabel: field });
      }
    }
  });
  return issues;
}

/** Per-line list of zero/missing `price_per_weight`. Structured twin of `hasZeroPriceInFlows`. */
export function findZeroPriceIssues(flows: PreOrderFlow[]): LineFieldIssue[] {
  const issues: LineFieldIssue[] = [];
  flows.forEach((f, i) => {
    if (f.status === PreOrderFlowStatus.CANCELLED) return;
    const v = f.price_per_weight;
    if (v == null || Number(v) === 0) {
      issues.push({
        lineNumber: i + 1,
        fieldKey: "price_per_weight",
        fieldLabel: "price_per_weight",
      });
    }
  });
  return issues;
}

export function getValidationSummary(preOrders: PreOrder[]) {
  const issues = validatePreOrders(preOrders);
  const totalErrors = issues.reduce(
    (sum, issue) => sum + issue.emptyFields.length,
    0,
  );
  return {
    total: preOrders.length,
    complete: preOrders.length - issues.length,
    incomplete: issues.length,
    totalErrors,
    issues,
  };
}
